import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { Box, useTheme } from '@mui/material'
import capitalize from 'lodash/capitalize'
import findKey from 'lodash/findKey'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router'
import { toast } from 'react-toastify'

import Button from '@/components/Button'
import CalibrationDialog from '@/components/CalibrationDialog'
import Dialog from '@/components/Dialog'
import { useAppSelector } from '@/hooks/redux'
import { useCalibration } from '@/hooks/useCalibration'
import { imageType } from '@/pages/getRecipe/allInOne/utils/constants'
import { clearAllInOneState } from '@/store/allInOne'
import {
  PLACEMENT,
  clearCalibrationInProgress,
  markCalibrationInProgress,
  removeImageById,
  setCanvasImage,
  setDualCanvasImage,
  setGreyCardValue,
} from '@/store/appSlice'
import {
  removeColorCheckerElementsByPlacement,
  removeSelectedColorCheckerShadeByPlacement,
} from '@/store/digitalTryInSlice'
import { setImageId } from '@/store/project'
import { DUAL_CANVAS_INCLUDED_PATHS } from '@/utils/constants'

import GalleryImage from './GalleryImage'

interface GalleryImageWrapperProps {
  image: any
  index: number
  setNewImages: any
  isLoading?: boolean
  triggerAutoCalibration?: boolean
}

const GalleryImageWrapper: React.FC<GalleryImageWrapperProps> = ({
  image,
  index,
  setNewImages,
  triggerAutoCalibration,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'gallery' })
  const theme = useTheme()
  const location = useLocation()
  const dispatch = useDispatch()

  const greyCardValue = useAppSelector((state) => state.app.greyCardValue)

  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isPlacementDialog, setIsPlacementDialog] = useState(false)
  const [isCalibrationDialog, setIsCalibrationDialog] = useState(false)

  const [selectedPlacement, setSelectedPlacement] = useState<PLACEMENT | null>(
    null,
  )
  const canvasImage = useAppSelector((state) => state.app.canvasImage)
  const dualCanvasImage = useAppSelector((state) => state.app.dualCanvasImage)
  const hiddenPaths = ['/shade-guide-ai']
  const shouldRenderDialog = !hiddenPaths.includes(location.pathname)
  const pendingCalibrationIds = useAppSelector(
    (state) => state.app.pendingCalibrationIds,
  )
  const isVerticallyAligned = useAppSelector(
    (state) => state.digitalTryIn.isVerticallyAligned,
  )

  const isDualCanvas = useMemo(
    () => DUAL_CANVAS_INCLUDED_PATHS?.includes(location.pathname),
    [location],
  )

  useEffect(() => {
    const canTriggerCalibration =
      !isDualCanvas || (isDualCanvas && selectedPlacement)
    if (
      shouldRenderDialog &&
      canTriggerCalibration &&
      triggerAutoCalibration &&
      !image.calibrated_image &&
      image.type === imageType.RAW &&
      !pendingCalibrationIds?.includes(image.id)
    ) {
      dispatch(markCalibrationInProgress(image.id))
      setIsCalibrationDialog(true)
    }
  }, [
    triggerAutoCalibration,
    image,
    pendingCalibrationIds,
    dispatch,
    isDualCanvas,
    selectedPlacement,
    shouldRenderDialog,
  ])

  const { handleCalibrationClick, isDisabled, resetCalibration } =
    useCalibration({
      imageId: image.id,
      canvasImage: image,
      value: greyCardValue[image.id] ?? '',
      onCalibrationComplete: (calibratedImage) => {
        dispatch(clearCalibrationInProgress(image.id))

        if (selectedPlacement) {
          dispatch(setDualCanvasImage({ [selectedPlacement]: calibratedImage }))
        }
        if (!isDualCanvas) {
          dispatch(setCanvasImage(calibratedImage))
        }
      },
    })

  const isSelected = useMemo(() => {
    if (isDualCanvas) {
      return Object.keys(dualCanvasImage)?.some(
        (key) =>
          dualCanvasImage?.[key as keyof typeof dualCanvasImage]?.id ===
          image?.id,
      )
    } else {
      return canvasImage?.id === image?.id
    }
  }, [canvasImage, dualCanvasImage, isDualCanvas, image])

  const galleryImageLodaingMap = useAppSelector(
    (state) => state.session.galleryImageLodaingMap,
  )

  const isIdentical =
    dualCanvasImage?.[PLACEMENT.LEFT]?.id ===
    dualCanvasImage?.[PLACEMENT.RIGHT]?.id

  const position = useMemo(
    () => (isDualCanvas ? findKey(dualCanvasImage, { id: image?.id }) : ''),
    [dualCanvasImage, image?.id, isDualCanvas],
  )

  let placementLabel = ''
  if (isVerticallyAligned) {
    if (position === 'left') {
      placementLabel = t('imagePlacementDialog.top')
    } else if (position === 'right') {
      placementLabel = t('imagePlacementDialog.bottom')
    }
  } else if (position) {
    placementLabel = capitalize(position)
  }

  const selectedIcon = useMemo(() => {
    if (isSelected) {
      if (isDualCanvas && !isIdentical) {
        return (
          <Box
            borderRadius="6.25rem"
            bgcolor="var(--color-accent-neongreen)"
            height="1.5rem"
            display="flex"
            alignItems="center"
            justifyContent="center"
            px={1.2}
            position="absolute"
            top="0.5rem"
            right="0.5rem"
            color={theme.palette.common.black}
            fontSize="0.8125rem"
            minWidth={'53px'}
          >
            {placementLabel}
          </Box>
        )
      }
      return (
        <CheckCircleIcon
          sx={{
            background: 'var(--color-accent-neongreen)',
            borderRadius: '100%',
            position: 'absolute',
            top: '0.5rem',
            right: '0.5rem',
          }}
        />
      )
    }
    return null
  }, [
    isSelected,
    isIdentical,
    isDualCanvas,
    theme.palette.common.black,
    placementLabel,
  ])

  const handleImageSelection = () => {
    resetCalibration()
    if (isDualCanvas) {
      setIsPlacementDialog(true)
    } else {
      dispatch(setCanvasImage(image))
      if (!image?.calibrated_image && image?.type === imageType.RAW) {
        setIsCalibrationDialog(true)
      }
      // else {
      //   dispatch(clearAllInOneState())
      // }
    }
  }

  const handlePlacementChange = (key: PLACEMENT) => {
    setIsPlacementDialog(false)
    setSelectedPlacement(key)
    if (!image?.calibrated_image && image?.type === imageType.RAW) {
      setIsCalibrationDialog(true)
    } else {
      dispatch(setDualCanvasImage({ [key]: image }))
      dispatch(clearAllInOneState())
    }
  }

  const handleLoadImage = () => {
    if (selectedPlacement) {
      dispatch(setDualCanvasImage({ [selectedPlacement]: image }))
      dispatch(clearAllInOneState())
    }
    if (!isDualCanvas) {
      dispatch(setCanvasImage(image))
    }

    handleCalibrationClick()
    setIsCalibrationDialog(false)
  }

  const handleImageDeletion = () => {
    dispatch(removeImageById(image.id))
    dispatch(setImageId(null))

    if (isSelected) {
      if (isDualCanvas && position) {
        dispatch(
          setDualCanvasImage(
            isIdentical
              ? { [PLACEMENT.LEFT]: null, [PLACEMENT.RIGHT]: null }
              : { [position]: null },
          ),
        )
      } else {
        dispatch(setCanvasImage(null))
      }
    }

    setIsDialogOpen(false)

    if (position) {
      dispatch(
        removeSelectedColorCheckerShadeByPlacement(position as PLACEMENT),
      )
      dispatch(removeColorCheckerElementsByPlacement(position as PLACEMENT))
    }
    toast.success(t('imageRemoved'))
  }

  const resetCalibrationDialog = () => {
    dispatch(setGreyCardValue({ imageId: image.id, value: '' }))
    setIsCalibrationDialog(false)
  }

  return (
    <>
      <GalleryImage
        image={image}
        index={index}
        isLoading={!galleryImageLodaingMap?.[image?.id]}
        handleImageDeletion={handleImageDeletion}
        handleImageSelection={handleImageSelection}
        selectedIcon={selectedIcon}
        isDialogOpen={isDialogOpen}
        setIsDialogOpen={setIsDialogOpen}
        isSelected={isSelected}
        isDualCanvas={isDualCanvas}
        isIdentical={isIdentical}
        setNewImages={setNewImages}
        onRecalibrate={() => setIsCalibrationDialog(true)}
        position={position as PLACEMENT}
      />
      <Dialog
        triggerElement={null}
        isOpen={isPlacementDialog}
        onClose={() => setIsPlacementDialog(false)}
        title={t('imagePlacementDialog.title')}
        content={null}
        paperStyles={{ width: '600px' }}
        actionButtons={
          <>
            <Button
              variant="contained"
              label={
                isVerticallyAligned
                  ? t('imagePlacementDialog.top')
                  : t('imagePlacementDialog.left')
              }
              fullWidth
              onClick={() => handlePlacementChange(PLACEMENT.LEFT)}
            />
            <Button
              variant="contained"
              label={
                isVerticallyAligned
                  ? t('imagePlacementDialog.bottom')
                  : t('imagePlacementDialog.right')
              }
              fullWidth
              onClick={() => handlePlacementChange(PLACEMENT.RIGHT)}
            />
          </>
        }
      />
      {shouldRenderDialog && (
        <CalibrationDialog
          isOpen={isCalibrationDialog}
          onClose={resetCalibrationDialog}
          onCalibrate={handleLoadImage}
          onGreyCardChange={(v) =>
            dispatch(setGreyCardValue({ imageId: image.id, value: v }))
          }
          disabled={isDisabled}
          imageId={image.id}
        />
      )}
    </>
  )
}

export default GalleryImageWrapper
